import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useIsMobile } from '../hooks/useIsMobile'
import { 
  ArrowLeft, 
  Calendar, 
  CheckCircle2, 
  Clock, 
  Database, 
  FileSearch, 
  Layers, 
  PhoneCall, 
  ShieldCheck, 
  Tag, 
  User, 
  Zap,
  ChevronDown,
  ChevronUp,
  History as HistoryIcon
} from 'lucide-react'
import api from '../services/api'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COLLECTION_CONFIG = {
  partnerLeads:   { label: 'Partner Lead',  color: 'var(--purple)', icon: <User size={14} /> },
  incomingLeads:  { label: 'Incoming Lead', color: 'var(--blue)',   icon: <Zap size={14} /> },
  sentleads:      { label: 'Outbound',      color: 'var(--amber)',  icon: <PhoneCall size={14} /> },
  acceptedleads:  { label: 'Accepted',      color: 'var(--green)',  icon: <CheckCircle2 size={14} /> },
  rejectedleads:  { label: 'Rejected',      color: 'var(--red)',    icon: <ShieldCheck size={14} /> },
  eligibleforsms: { label: 'Eligible SMS',  color: 'var(--purple)', icon: <Tag size={14} /> },
  postbackLogs:   { label: 'Postback',      color: 'var(--green)',  icon: <HistoryIcon size={14} /> },
  leadlogs:       { label: 'Internal Log',  color: 'var(--text-muted)', icon: <Layers size={14} /> },
  lead_revenue_events: { label: 'Revenue Event', color: 'var(--amber)', icon: <Database size={14} /> },
}

const REJECTION_LABELS = {
  duplicate:        'Duplicate Lead',
  invalid_payload:  'Invalid Payload',
  invalid_token:    'Invalid Token',
  suppressed_phone: 'Suppressed Phone',
  validation_error: 'Validation Error',
  processing_error: 'Processing Error',
}

function fmt(dt) {
  if (!dt) return '—'
  return new Date(dt).toLocaleString('en-US', {
    month: 'short', day: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

// ─── Evento del timeline ──────────────────────────────────────────────────────

function TimelineEvent({ event }) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const cfg = COLLECTION_CONFIG[event.collection] || { label: event.collection, color: 'var(--text-muted)', icon: <Database size={14} /> }
  const s   = event.summary || {}

  return (
    <div className="timeline-item">
      <div className="timeline-dot" style={{ borderColor: cfg.color }} />
      <div className="timeline-card">
        <div className="timeline-header" onClick={() => setOpen(!open)} style={{ flexWrap: 'wrap', gap: 8, rowGap: 8 }}>
          <div className="timeline-title" style={{ minWidth: 0, flexWrap: 'wrap' }}>
            <div style={{ 
              width: 32, height: 32, borderRadius: 10, 
              background: cfg.color + '15', color: cfg.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              {cfg.icon}
            </div>
            <span>{event.collection === 'leadlogs' && s.kind ? s.kind.replace(/_/g, ' ') : cfg.label}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--bg-raised)', padding: '2px 8px', borderRadius: 99 }}>
              {event.collection}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span className="timeline-time">{fmt(event.timestamp)}</span>
            {open ? <ChevronDown size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" style={{ transform: 'rotate(-90deg)' }} />}
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
          {s.status && <span className={`badge ${s.status === 'accepted' ? 'badge-green' : s.status === 'rejected' ? 'badge-red' : 'badge-amber'}`}>{s.status}</span>}
          {s.partner_aff_id && <Chip icon={<User size={10} />} label="Partner" value={s.partner_aff_id} />}
          {s.rejection_reason && <Chip icon={<ShieldCheck size={10} />} label="Reason" value={REJECTION_LABELS[s.rejection_reason] || s.rejection_reason} color="var(--red)" />}
          {s.lender && <Chip icon={<Database size={10} />} label="Lender" value={s.lender} />}
          {s.http_status && <Chip label="HTTP" value={s.http_status} color={s.http_status >= 200 && s.http_status < 300 ? 'var(--green)' : 'var(--red)'} />}
          {s.status_code && <Chip label="Status Code" value={s.status_code} color={s.status_code >= 200 && s.status_code < 300 ? 'var(--green)' : 'var(--red)'} />}
          {s.success !== undefined && <Chip label="Success" value={s.success ? 'True' : 'False'} color={s.success ? 'var(--green)' : 'var(--red)'} />}
          {s.elapsed && <Chip icon={<Clock size={10} />} label="Time" value={Number(s.elapsed).toFixed(2) + 's'} />}
        </div>

        {open && (
          <div className="timeline-content">
            <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, borderBottom: '1px solid var(--border)', paddingBottom: 8 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', wordBreak: 'break-all' }}>Document ID: {event.document_id}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Type: {event.collection}</span>
            </div>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowX: 'auto', fontSize: isMobile ? 11 : undefined }}>
              {JSON.stringify(s, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}

function Chip({ icon, label, value, color }) {
  return (
    <span style={{ 
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: 'var(--bg-base)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '4px 10px', fontSize: 11, fontWeight: 500,
      maxWidth: '100%'
    }}>
      {icon && <span style={{ color: 'var(--text-muted)' }}>{icon}</span>}
      <span style={{ color: 'var(--text-muted)' }}>{label}:</span>
      <span style={{ color: color || 'var(--text-primary)', wordBreak: 'break-all' }}>{String(value)}</span>
    </span>
  )
}

// ─── Página principal ──────────────────────────────────────────────────────────

export default function LeadTimelinePage() {
  const isMobile = useIsMobile()
  const { kickoffLeadId } = useParams()
  const navigate          = useNavigate()

  const [timeline, setTimeline] = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  useEffect(() => {
    if (!kickoffLeadId) return
    setLoading(true)
    api.get(`/leads/timeline/${kickoffLeadId}`)
      .then(res => setTimeline(res.data))
      .catch(e  => setError(e.response?.data?.detail || 'Error loading timeline.'))
      .finally(() => setLoading(false))
  }, [kickoffLeadId])

  if (loading) return (
    <div className="page-shell page-shell-center">
      <div className="spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
    </div>
  )

  if (error) return (
    <div className="page-shell">
      <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
        <ArrowLeft size={16} /> Back to Leads
      </button>
      <div className="card" style={{ borderLeft: '4px solid var(--red)', color: 'var(--red)' }}>
        <strong>Error:</strong> {error}
      </div>
    </div>
  )

  if (!timeline) return null

  const norm       = timeline.normalized || {}
  const statusColors = { accepted: 'var(--green)', rejected: 'var(--red)', pending: 'var(--amber)' }
  const finalColor = statusColors[timeline.final_status] || 'var(--text-muted)'

  return (
    <div className="fade-in page-shell">
      
      {/* Header */}
      <section className="page-header" style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 16 }}>
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)} style={{ flexShrink: 0 }}>
          <ArrowLeft size={18} />
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontFamily: 'monospace', fontSize: isMobile ? 18 : 24, wordBreak: 'break-all' }}>{kickoffLeadId}</h1>
            <span className="badge badge-gray" style={{ fontSize: 12 }}>Timeline Mode</span>
          </div>
          <p style={{ marginTop: 4 }}>Full lifecycle tracking for this lead across all system components.</p>
        </div>
      </section>

      {/* Summary Area */}
      <div className="data-grid" style={{ marginBottom: 32, ...(isMobile ? { gridTemplateColumns: '1fr' } : {}) }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <User size={16} color="var(--accent)" />
            <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Partner Entity</span>
          </div>
          <div className="data-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="data-item">
              <div className="data-label">Affiliate ID</div>
              <div className="data-value">{timeline.partner_aff_id || '—'}</div>
            </div>
            <div className="data-item">
              <div className="data-label">External Reference</div>
              <div className="data-value" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{timeline.partner_id_externo || '—'}</div>
            </div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileSearch size={16} color="var(--accent)" />
            <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lead Context</span>
          </div>
          <div className="data-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="data-item">
              <div className="data-label">Contact Name</div>
              <div className="data-value">{norm.full_name || [norm.first_name, norm.last_name].filter(Boolean).join(' ') || '—'}</div>
            </div>
            <div className="data-item">
              <div className="data-label">Email Address</div>
              <div className="data-value" style={{ fontSize: 13, wordBreak: 'break-all' }}>{norm.email || '—'}</div>
            </div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Zap size={16} color={finalColor} />
            <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Final Resolution</span>
          </div>
          <div className="data-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="data-item">
              <div className="data-label">Process Status</div>
              <div className="data-value" style={{ color: finalColor, fontSize: 18, textTransform: 'capitalize' }}>
                {timeline.final_status || 'In Progress'}
              </div>
            </div>
            <div className="data-item">
              <div className="data-label">Winning Lender</div>
              <div className="data-value" style={{ fontSize: 13, fontWeight: 700 }}>
                {timeline.lender_accepted || 'Waiting/None'}
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryIcon size={16} color={timeline.postback_success ? 'var(--green)' : (timeline.postback_status ? 'var(--red)' : 'var(--text-muted)')} />
            <span style={{ fontWeight: 800, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Postback Status</span>
          </div>
          <div className="data-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="data-item">
              <div className="data-label">Delivery Result</div>
              <div className="data-value" style={{ 
                color: timeline.postback_success ? 'var(--green)' : (timeline.postback_status ? 'var(--red)' : 'var(--text-muted)'),
                fontSize: 16, fontWeight: 700
              }}>
                {timeline.postback_success ? 'Delivered' : (timeline.postback_status ? `Error (${timeline.postback_status})` : 'Pending/Skip')}
              </div>
            </div>
            <div className="data-item">
              <div className="data-label">Postback HTTP</div>
              <div className="data-value" style={{ fontSize: 13 }}>
                {timeline.postback_status || '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline List */}
      <section className="card" style={{ padding: isMobile ? '20px 16px' : '32px 40px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, marginBottom: 28, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <HistoryIcon size={20} color="var(--accent)" />
          Execution Timeline
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, background: 'var(--bg-base)', padding: '4px 12px', borderRadius: 99 }}>
            {timeline.events?.length || 0} Events
          </span>
        </h3>

        <div className="timeline-container">
          <div className="timeline-line" />
          
          {(!timeline.events || timeline.events.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
              No execution events recorded for this lead ID.
            </div>
          ) : (
            timeline.events.map((event, i) => (
              <TimelineEvent
                key={`${event.collection}-${i}`}
                event={event}
              />
            ))
          )}
        </div>
      </section>

      {/* Technical Data Tags */}
      <div style={{ marginTop: 24, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {[
          { flag: timeline.reached_incoming,  label: 'Incoming Pipeline' },
          { flag: timeline.reached_sent,      label: 'Outbound Delivery' },
          { flag: timeline.reached_accepted,  label: 'Lender Acceptance' },
          { flag: timeline.reached_rejected,  label: 'Negative Decision' },
          { flag: timeline.reached_moneylion, label: 'MoneyLion Stack' },
          { flag: timeline.reached_sms,       label: 'SMS Channel' },
        ].filter(f => f.flag).map(f => (
          <span key={f.label} className="badge badge-blue" style={{ padding: '6px 14px', fontSize: 11 }}>
            {f.label}
          </span>
        ))}
      </div>
    </div>
  )
}
